import {FieldBlock, FormBlock, FormDefinition, GroupBlock, ListBlock} from '../models/form-schema.model';
import {SubmissionFieldInput, SubmissionGroupInput, SubmitSubmissionRequest} from '../models/submit-request.model';
import {isFileRef, Submission} from '../models/submission.model';
import {blockPathSegment, evaluateFormVisibility, pathId} from './visibility-evaluator';

/** The group key carrying every non-repeating field. Repeating rows use the list's own key. */
export const DEFAULT_GROUP_KEY = 'default';

/** Every `list` block key in a definition, at any depth. */
export function listKeysOf(definition: FormDefinition): Set<string> {
  const keys = new Set<string>();
  const walk = (blocks: FormBlock[]) => {
    for (const block of blocks) {
      if (block.type === 'list') keys.add((block as ListBlock).key);
      if ('blocks' in block) walk((block as { blocks: FormBlock[] }).blocks);
    }
  };
  definition.pages.forEach(page => walk(page.blocks));
  return keys;
}

/**
 * Separator for {@link storedKeyId}. A character no group key, row index or field key can contain, so two
 * different addresses can never collide into one id.
 */
const STORED_KEY_SEPARATOR = '\u0000';

/**
 * The wire address of one stored answer: its group, that group's row index, and the field key.
 *
 * Row index is part of the identity on purpose — a repeating list stores one `SubmissionGroup` per row, so
 * `jobs[0].befristet_bis` and `jobs[1].befristet_bis` are two different stored values and exactly one of them
 * may need clearing.
 */
export function storedKeyId(groupKey: string, index: number, fieldKey: string): string {
  return [groupKey, index, fieldKey].join(STORED_KEY_SEPARATOR);
}

/**
 * The addresses a loaded submission already stores a value for — feeds {@link BuildSubmitOptions.stored}.
 *
 * Every stored field counts, including one whose value is masked or empty: what matters is that a row exists
 * server-side which an omitted key would leave untouched.
 */
export function storedFieldKeys(submission: Submission | null | undefined): Set<string> {
  const keys = new Set<string>();
  for (const group of submission?.groups ?? []) {
    for (const value of group.values) keys.add(storedKeyId(group.groupKey, group.index, value.fieldKey));
  }
  return keys;
}

/**
 * Re-groups the flat address set back into `groupKey → index → fieldKeys[]`.
 *
 * Parsing our own {@link storedKeyId} format is safe by construction: the separator is a character no group key,
 * row index or field key can contain, so a split can never mis-attribute a segment. Built once per
 * {@link buildSubmitRequest} call and used only to find the stored **rows** a list no longer covers (#479).
 */
function indexStoredRows(stored: ReadonlySet<string> | undefined): Map<string, Map<number, string[]>> {
  const byGroup = new Map<string, Map<number, string[]>>();

  for (const id of stored ?? []) {
    const parts = id.split(STORED_KEY_SEPARATOR);
    if (parts.length !== 3) continue;
    const [groupKey, rawIndex, fieldKey] = parts;
    const index = Number(rawIndex);
    if (!Number.isInteger(index)) continue;

    let rows = byGroup.get(groupKey);
    if (!rows) byGroup.set(groupKey, rows = new Map<number, string[]>());
    const fieldKeys = rows.get(index);
    if (fieldKeys) fieldKeys.push(fieldKey);
    else rows.set(index, [fieldKey]);
  }

  return byGroup;
}

/**
 * The `file`-typed keys anywhere under `blocks`.
 *
 * A dropped row's attachment cannot be removed with a JSON `null`: the server's merge skips every `file`
 * descriptor outright (attachments ride the dedicated file endpoints), so emitting one would be a no-op that
 * only makes the payload noisier. See {@link buildSubmitRequest}'s note on the residue this leaves.
 */
function fileKeysOf(blocks: FormBlock[]): Set<string> {
  const keys = new Set<string>();
  const walk = (candidates: FormBlock[]) => {
    for (const block of candidates) {
      if (block.type === 'file') keys.add((block as FieldBlock).key);
      else if ('blocks' in block) walk((block as { blocks: FormBlock[] }).blocks);
    }
  };
  walk(blocks);
  return keys;
}

/** Options for {@link buildSubmitRequest}. */
export interface BuildSubmitOptions {
  /**
   * `wire address → masked display value` for sensitive fields shown masked (from a loaded submission, via
   * {@link maskedFieldMap}). When a field's current value still equals the mask stored **at its own address**,
   * it was not edited → send `keep: true` so the server keeps the stored ciphertext instead of overwriting it
   * with the mask string. Used when revising a submission.
   *
   * Addressed by `(groupKey, index, fieldKey)` since #482 — keyed by the bare field key, two rows of a
   * repeating list carrying the same sensitive field collided and the last one won.
   */
  masked?: Record<string, unknown>;
  /**
   * The addresses the submission **being revised** already stores a value for (see {@link storedFieldKeys}).
   *
   * Only set this when updating an existing submission — a first fill must leave it absent, because on a create
   * there is nothing to clear and a hidden field is simply omitted.
   *
   * **Why it is needed at all.** `SubmissionController.UpdateCoreAsync` merges: it adds or updates the keys the
   * request carries and never removes anything, so to it an **absent key means "leave the stored value alone"**.
   * Combine that with #470's "a hidden field is omitted" and a revision goes wrong in a way nobody sees: answer
   * a question, save, then change an upstream answer so the question no longer applies — the field is now
   * omitted, the stored answer survives, and it resurfaces in the management detail view, the PDF and the
   * retained record as an answer to a question that was never asked. That is worse than the "–" rows #470
   * removed. With this set, such a field is sent as an explicit clear instead of being omitted.
   */
  stored?: ReadonlySet<string>;
}

/**
 * Maps a {@link DynamicFormComponent} raw value (nested by group key, as built by
 * {@link FormFactoryService}) into a {@link SubmitSubmissionRequest}.
 *
 * The Forms service matches submitted fields by their (globally unique) `fieldKey` and flattens all groups for
 * validation/persistence, so we emit every leaf field into a single `default` group. We walk the *definition*
 * (not the raw value) so the structure is authoritative and untouched fields still post their default value.
 *
 * **file** fields emit no entry, deliberately and permanently: an attachment is never a JSON value. Its bytes
 * ride as their own `file:<fieldKey>` multipart part (see {@link SubmissionHttpService.submit}) and the server
 * derives "this field was answered" from that part, not from `values[]`. Sending the file *name* here would be
 * worse than sending nothing — it would store a bare string where an attachment reference belongs (#258).
 *
 * **list** blocks (repeating rows) emit **one group per row**, keyed by the list's own key and carrying the
 * row's 0-based `index` — the shape `SubmissionGroup` was always modelled for. Non-repeating fields all live in
 * the single {@link DEFAULT_GROUP_KEY} group, so a form without lists produces exactly the payload it did
 * before (#259).
 *
 * **A hidden field is not submitted as a value (#470)** — but what "not submitted" means depends on whether
 * anything is already stored for it:
 *
 * - **Nothing stored (a first fill, or {@link BuildSubmitOptions.stored} not supplied)** ⇒ the field emits
 *   **nothing at all**. A control hidden by `visibleWhen` is *disabled*, not cleared, so `getRawValue()` still
 *   yields its `''` — and the Forms service then ran `value.Value<double>()` over an empty string for a `number`
 *   field and answered HTTP 500. Omission (rather than a `null`) is the product owner's decision: the field does
 *   not apply, so there is nothing to record.
 * - **Already stored (a revision / draft update)** ⇒ the field emits an explicit **clear**
 *   (`{fieldKey, value: null, keep: false}`). Omitting it here would not mean "no answer", it would mean
 *   "leave the stored answer alone": `SubmissionController.UpdateCoreAsync` only ever adds or updates the keys
 *   the request carries and has no removal step. The old answer would survive and resurface in the detail view,
 *   the PDF and the retained record as an answer to a question that no longer applies.
 *
 * A field hidden only because its enclosing `group` / `list` block is hidden counts as hidden too — the walk
 * still descends into a hidden container, precisely so its stored children can be cleared — and one hidden field
 * inside a visible list row drops (or clears) just that row's entry.
 *
 * **A deleted repeating row is cleared the same way (#479).** The merge has no removal step for a whole
 * `SubmissionGroup` either, so a row the employee deletes on a revision would keep its stored answers. Every
 * stored row index this request does not write gets a group of explicit clears. The rule is deliberately about
 * the **index**, not about changed content: `FormArray.removeAt` re-indexes, so the surviving rows always submit
 * as a dense `0..n-1` and overwrite those stored indices themselves — only the tail beyond the new row count is
 * orphaned. A pure **reorder** therefore clears nothing, which is correct: every stored index is rewritten.
 *
 * **Masked sensitive values follow the ROW, not the field name (#482).** `options.masked` is addressed by
 * `(groupKey, index, fieldKey)`, so `keep: true` is emitted only when a field still shows the mask stored for
 * *that* row. What this deliberately does **not** solve: deleting a list row re-indexes the survivors, so a
 * row can end up holding a mask that belongs to a different stored address. The client has no plaintext and the
 * wire has no "move this ciphertext to another row" verb, so it cannot re-bind them. It refuses to make things
 * worse instead — a value that is a mask is never written back as a literal (that would replace a real
 * encrypted answer with the mask text), it is kept. The consequence to know: after deleting a row above them,
 * the *masked* values of the rows below stay with their index while the rest of the row's answers move up.
 * Fixing that properly needs a server-side row-move.
 *
 * Two things that row-clearing deliberately does not do:
 * - **The row shell stays.** The stored `GroupInstance` remains server-side with all-null values rather than
 *   being deleted; there is no delete-a-group verb, and every read-back view now skips a field with no value, so
 *   an empty shell is invisible. Do not read a surviving group as "the employee kept this row".
 * - **An attachment in a dropped row is not removed.** The merge skips every `file` descriptor (attachments ride
 *   the dedicated file endpoints), so a JSON `null` would be a no-op; those keys are left out of the clear
 *   entirely. The blob is orphaned until the retention sweep takes it.
 *
 * The hidden set is computed from the **definition + values** ({@link evaluateFormVisibility}), never from
 * `control.disabled`: that keeps this function a pure mapper (no live `FormGroup` needed to test it) and, more
 * importantly, keeps `enabledWhen` out of it — an `enabledWhen`-disabled field *applies*, it is merely read-only,
 * and it still submits its value.
 *
 * The consequence the product owner accepted: a **visible** field the employee left blank and a field that was
 * hidden are afterwards indistinguishable to the read-back views (both render as absent).
 *
 * Not supported: a `list` nested inside another `list`. The inner rows of every outer row would collide on the
 * same `(groupKey, index)` pair, so they are emitted but not distinguishable — keep lists one level deep.
 */
export function buildSubmitRequest(
  definition: FormDefinition, rawValue: any, finalize = true, options?: BuildSubmitOptions): SubmitSubmissionRequest {
  const rootValues: SubmissionFieldInput[] = [];
  const rowGroups: SubmissionGroupInput[] = [];
  const masked = options?.masked;
  const stored = options?.stored;
  /** `listKey → index → stored field keys`, for the rows the employee deleted (#479). */
  const storedRows = indexStoredRows(stored);
  /**
   * Every masked display value stored for a field key, at any address — see the mask-drift guard in the walk.
   */
  const maskValuesByField = new Map<string, Set<unknown>>();
  for (const [id, value] of Object.entries(masked ?? {})) {
    const fieldKey = id.split(STORED_KEY_SEPARATOR)[2];
    if (fieldKey === undefined) continue;
    const values = maskValuesByField.get(fieldKey);
    if (values) values.add(value);
    else maskValuesByField.set(fieldKey, new Set([value]));
  }

  const verdict = evaluateFormVisibility(definition, rawValue);
  /** Unknown paths default to *visible*: never silently drop an answer the evaluator did not reach. */
  const isHidden = (path: string[]) => verdict.byPath.get(pathId(path))?.visible === false;

  /**
   * @param groupKey  the wire group this scope's leaf fields belong to — {@link DEFAULT_GROUP_KEY} for
   *                  everything non-repeating (a `group` block is flattened into it), the list's key inside a row
   * @param groupIndex that group's row index (always 0 outside a list)
   * @param hidden    true once an enclosing block does not apply; the walk still descends, so a stored child can
   *                  be cleared rather than silently left behind
   */
  const walk = (
    blocks: FormBlock[],
    scope: any,
    into: SubmissionFieldInput[],
    parentPath: string[],
    groupKey: string,
    groupIndex: number,
    hidden: boolean) => {

    for (const block of blocks) {
      if (block.type === 'static-text') continue;
      // Two different things, deliberately not one variable (#496): `key` addresses the *data* (the control name
      // in the raw value, and the wire `fieldKey`/`groupKey`), while the path segment addresses the *verdict*.
      // They coincide for every block that has a key — but the evaluator writes its entries under
      // `blockPathSegment` (`key || id`), and `isHidden` treats an unmatched path as NOT hidden, so a keyless
      // block would have submitted an answer to a question that was never asked.
      const key = (block as { key: string }).key;
      const path = [...parentPath, blockPathSegment(block)];
      // `hidden ||` is belt-and-braces: the evaluator already propagates a hidden container to its subtree, but
      // this way one predicate cannot quietly disagree with the other.
      const blockHidden = hidden || isHidden(path);

      if (block.type === 'group') {
        // Group values are nested one level deeper under the group key (see FormFactoryService.addBlocksToGroup).
        walk((block as GroupBlock).blocks, scope?.[key] ?? {}, into, path, groupKey, groupIndex, blockHidden);
      } else if (block.type === 'list') {
        // One group per row. Rows the employee never added simply produce no groups.
        const listBlock = block as ListBlock;
        const rows: unknown[] = Array.isArray(scope?.[key]) ? scope[key] : [];
        /** The row indices this request actually writes — the orphan rule below is defined against these. */
        const emitted = new Set<number>();

        rows.forEach((row, index) => {
          const values: SubmissionFieldInput[] = [];
          walk(listBlock.blocks, row ?? {}, values, [...path, String(index)], key, index, blockHidden);
          // A hidden list contributes only the clears its stored rows need. Pushing an empty group would create
          // a bare `GroupInstance` server-side for a row that does not apply.
          if (!blockHidden || values.length > 0) {
            rowGroups.push({groupKey: key, index, values});
            emitted.add(index);
          }
        });

        // Rows the employee DELETED (#479). Same merge cause as a hidden field: the row is simply not walked any
        // more, and an absent key means "leave the stored value alone" — so its answers would survive into the
        // detail view, the PDF and the retained record. Clear every stored row index this request does not write.
        //
        // Keying on the INDEX (not on changed content) is what makes this correct under deletion *and*
        // reordering: `FormArray.removeAt` re-indexes, so the surviving rows always submit as a dense 0..n-1 and
        // overwrite those stored indices by themselves. Only the tail beyond the new row count is orphaned, and
        // a pure reorder writes every stored index, so it clears nothing.
        const orphans = storedRows.get(key);
        if (orphans) {
          const fileKeys = fileKeysOf(listBlock.blocks);
          for (const [index, fieldKeys] of orphans) {
            if (emitted.has(index)) continue; // already written above — never clear an address twice
            const values: SubmissionFieldInput[] = fieldKeys
              .filter(fieldKey => !fileKeys.has(fieldKey))
              .map(fieldKey => ({fieldKey, value: null, keep: false}));
            // Nothing clearable ⇒ no group. The stored group already exists server-side, so this is not about
            // minting a bare GroupInstance — it is about not sending a payload that changes nothing.
            if (values.length > 0) rowGroups.push({groupKey: key, index, values});
          }
        }
      } else if (block.type === 'file') {
        continue; // an attachment is a multipart part, never a JSON value — see the doc comment above
      } else if (blockHidden) {
        // The question does not apply. Omit it — unless the submission being revised already stores an answer,
        // which an omitted key would leave untouched (see BuildSubmitOptions.stored).
        if (stored?.has(storedKeyId(groupKey, groupIndex, key))) {
          into.push({fieldKey: key, value: null, keep: false});
        }
      } else {
        const field = block as FieldBlock;
        const current = scope?.[field.key] ?? null;
        const maskId = storedKeyId(groupKey, groupIndex, field.key);

        if (masked && maskId in masked && current === masked[maskId]) {
          // Still showing the mask stored for THIS row → untouched → keep that row's own ciphertext.
          into.push({fieldKey: field.key, value: null, keep: true});
        } else if (maskValuesByField.get(field.key)?.has(current)) {
          // The value is a mask, but one stored at a DIFFERENT address — the row's content shifted under it
          // (deleting a list row re-indexes the survivors). The renderer's edit gate means a typed value can
          // never coincidentally equal a mask, so this is never a real answer. Sending it would store the
          // literal mask text over a real encrypted value, so keep instead. See the mask-drift note on
          // buildSubmitRequest for what this deliberately does NOT solve.
          into.push({fieldKey: field.key, value: null, keep: true});
        } else {
          into.push({fieldKey: field.key, value: current, keep: false});
        }
      }
    }
  };

  definition.pages.forEach(page =>
    walk(page.blocks, rawValue ?? {}, rootValues, [], DEFAULT_GROUP_KEY, 0, false));

  return {
    formKey: definition.id,
    finalize,
    groups: [{groupKey: DEFAULT_GROUP_KEY, index: 0, values: rootValues}, ...rowGroups]
  };
}

/**
 * Builds a {@link DynamicFormComponent} `initialValue` (nested by group key) from a loaded {@link Submission}.
 * The submission stores fields flat by `fieldKey`; we walk the definition to place each value at its nested path.
 * Masked sensitive values are placed as their mask string (so the form shows them) — {@link buildSubmitRequest}'s
 * `masked` option then turns untouched masks back into `keep`.
 *
 * A **file** field is prefilled with the stored attachment's file *name*, matching what the renderer shows after
 * a pick. That is what makes resuming a draft that already carries an attachment work: the field reads as
 * answered (so the client's `required` validator passes), no new part is sent, and the server keeps the stored
 * blob untouched. Fields with no attachment are left alone.
 *
 * **list** rows are rebuilt from the groups keyed by the list, ordered by their stored `index` so the employee
 * sees them back in the order they entered them. The resulting array only becomes visible rows once
 * {@link FormFactoryService.syncListRows} has grown the `FormArray` — `patchValue` cannot create rows by itself.
 */
export function submissionToInitialValue(definition: FormDefinition, submission: Submission): any {
  const listKeys = listKeysOf(definition);
  const flat: Record<string, unknown> = {};
  const rowsByKey = new Map<string, Record<string, unknown>[]>();

  for (const group of submission.groups) {
    if (listKeys.has(group.groupKey)) {
      const row: Record<string, unknown> = {};
      for (const value of group.values) row[value.fieldKey] = value.value;
      const rows = rowsByKey.get(group.groupKey) ?? [];
      // Index-addressed, not appended: a gap (a row deleted server-side) must not shift the others.
      rows[group.index] = row;
      rowsByKey.set(group.groupKey, rows);
    } else {
      for (const value of group.values) flat[value.fieldKey] = value.value;
    }
  }

  const result: any = {};
  const walk = (blocks: FormBlock[], target: any, source: Record<string, unknown>) => {
    for (const block of blocks) {
      if (block.type === 'static-text') continue;
      const key = (block as { key: string }).key;
      if (block.type === 'group') {
        target[key] = {};
        walk((block as GroupBlock).blocks, target[key], source);
      } else if (block.type === 'list') {
        const rows = rowsByKey.get(key);
        if (!rows) continue;
        target[key] = rows.filter(Boolean).map(row => {
          const rowTarget: any = {};
          walk((block as ListBlock).blocks, rowTarget, row);
          return rowTarget;
        });
      } else if (block.type === 'file') {
        const stored = source[key];
        if (isFileRef(stored)) target[key] = stored.fileName;
      } else if (key in source) {
        target[key] = source[key];
      }
    }
  };

  definition.pages.forEach(page => walk(page.blocks, result, flat));
  return result;
}

/**
 * `wire address → masked value` for the submission's masked sensitive fields (feeds
 * {@link BuildSubmitOptions.masked}).
 *
 * Keyed by the full {@link storedKeyId}, not by the bare field key (#482). Flattening every group into one
 * `fieldKey → value` map made two rows of a repeating list that carry the same sensitive field collide, and the
 * last row won — so `buildSubmitRequest` compared a row against **another row's** mask and could send
 * `keep: true` for the wrong one, binding stored ciphertext to a row it no longer belonged to.
 */
export function maskedFieldMap(submission: Submission): Record<string, unknown> {
  const masked: Record<string, unknown> = {};
  for (const group of submission.groups) {
    for (const value of group.values) {
      if (value.isMasked) masked[storedKeyId(group.groupKey, group.index, value.fieldKey)] = value.value;
    }
  }
  return masked;
}

/**
 * The **bare** field keys that are masked, for {@link MaskedFieldsState}.
 *
 * Deliberately a different shape from {@link maskedFieldMap}: the field renderer edit-gates by *field*, not by
 * row — every row of a masked field renders the same read-only affordance — while the submit mapper has to
 * decide per row. One map cannot serve both without reintroducing the #482 collision.
 */
export function maskedFieldKeys(submission: Submission): Set<string> {
  const keys = new Set<string>();
  for (const group of submission.groups) {
    for (const value of group.values) {
      if (value.isMasked) keys.add(value.fieldKey);
    }
  }
  return keys;
}


/**
 * The conditional-logic verdict for a definition's `file` fields alone, as **bare field keys**.
 *
 * `file` blocks are ordinary leaf fields to `evaluateFormVisibility` — they have always been in the walk, and
 * always carried a real `visible`. What kept going wrong is that the attachment surfaces sit *outside* the
 * wizard and each asked the question their own way, so this reduction is expressed once and shared (#503).
 *
 * A `file` key may legitimately be emitted at more than one address (a `file` inside a repeating list yields one
 * entry per row), so the two sets are **not** complements: a key visible at any address lands in `applies`, and
 * only `hidden \ applies` is genuinely not being asked for.
 */
interface FileFieldVerdict {
  /** Keys the definition currently asks for — visible at ≥ 1 address. */
  applies: Set<string>;
  /** Keys reported hidden at ≥ 1 address. Subtract {@link applies} before concluding anything. */
  hidden: Set<string>;
}

/** One pass over the verdict, reduced to the `file` fields. See {@link FileFieldVerdict}. */
function fileFieldVerdict(definition: FormDefinition, rawValue: any): FileFieldVerdict {
  const applies = new Set<string>();
  const hidden = new Set<string>();
  for (const field of evaluateFormVisibility(definition, rawValue).fields) {
    if (field.block.type !== 'file') continue;
    (field.visible ? applies : hidden).add(field.key);
  }
  return {applies, hidden};
}

/**
 * The `file` field keys the form currently **asks for**, given `rawValue` (#503).
 *
 * This is the predicate an attachment surface must gate on. A `file` field whose `visibleWhen` is false is a
 * question the form is not asking — requesting a document for it is not a cosmetic slip: it tells an employee to
 * hand over a Rentenbescheid or an Immatrikulationsbescheinigung that their answers say they do not have.
 *
 * It is deliberately the *same* reduction {@link withoutHiddenFiles} filters the outgoing bytes with, so "shown
 * in the panel" and "transmitted with the submission" cannot drift apart — that drift is the whole class of
 * defect here (#494 fixed the transmit half; the request half was still unfiltered).
 *
 * @param definition the definition being filled in — the FULL one, including any `file` blocks a surface strips
 *                   out of the wizard it renders
 * @param rawValue   the form's current raw value
 */
export function requestedFileFieldKeys(definition: FormDefinition, rawValue: any): ReadonlySet<string> {
  return fileFieldVerdict(definition, rawValue).applies;
}

/**
 * Drops the picked attachments whose `file` field no longer applies (#494).
 *
 * `buildSubmitRequest` skips `file` blocks *before* the hidden check, deliberately — an attachment rides as its
 * own multipart part, never as a JSON value — so the visibility rules that omit a hidden field's answer never
 * reached the bytes. `SelectedFilesState` is only emptied by an explicit clear or by `reset()` after a
 * successful submit, so a document picked while the question applied was still uploaded after the employee
 * changed the upstream answer: every text answer correctly dropped, the personal document transmitted and
 * stored anyway. That is a data-minimisation failure, not untidiness.
 *
 * **Filtered at the boundary, not purged from state.** The pick stays in `SelectedFilesState` so that flipping
 * the upstream answer back restores it — silently losing the employee's file would be its own bug, and
 * visibility is recomputed on every keystroke, so purging would be both lossy and racy.
 *
 * Keyed by bare field key, matching `SelectedFilesState`. A `file` inside a repeating row is therefore not
 * addressed per row — the same limitation the state itself has, and no shipped template puts one there.
 *
 * @param definition the definition being submitted
 * @param rawValue   the form's raw value, as passed to {@link buildSubmitRequest}
 * @param files      `fieldKey → File` from `SelectedFilesState.snapshot()`
 */
export function withoutHiddenFiles<T>(
  definition: FormDefinition, rawValue: any, files: Record<string, T>): Record<string, T> {
  if (Object.keys(files).length === 0) return files;

  const {applies, hidden} = fileFieldVerdict(definition, rawValue);

  return Object.fromEntries(
    // A key the definition does not describe as a file field is left alone rather than guessed at; only a field
    // the verdict positively reports as hidden (and never also visible at another address) is withheld.
    Object.entries(files).filter(([key]) => !(hidden.has(key) && !applies.has(key))));
}
